// ---------------------------------------------------------------------------
// C122-C133: Builder UX Hooks — scroll animations, performance, GBP, interview, review
// ---------------------------------------------------------------------------
import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

function toErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return fallback;
}

function buildUnsupportedBuilderError(message: string): Error {
  return new Error(message);
}

// ---------------------------------------------------------------------------
// C122: Scroll animations config
// ---------------------------------------------------------------------------

export type AnimationType = "fade-in" | "slide-up" | "slide-left" | "slide-right" | "scale-up" | "blur-in";

export interface ScrollAnimationConfig {
  type: AnimationType;
  delay: number;
  duration: number;
  threshold: number;
  staggerChildren: boolean;
}

// ---------------------------------------------------------------------------
// C123: Page performance (Lighthouse + CWV)
// ---------------------------------------------------------------------------

export interface PagePerformance {
  pageId: string;
  pageName: string | null;
  performanceScore: number | null;
  accessibilityScore: number | null;
  seoScore: number | null;
  bestPracticesScore: number | null;
  lcpMs: number | null;
  tbtMs: number | null;
  /** @deprecated Legacy API field; it is lab TBT until field INP is wired. */
  inpMs: number | null;
  cls: number | null;
  fcpMs: number | null;
  ttfbMs: number | null;
  auditedAt: string | null;
  recommendations: Array<{ category: string; message: string; impact: "high" | "medium" | "low" }>;
}

interface OptimizationAnalysisResponse {
  pageId: string | null;
  performance: {
    audit: {
      performanceScore: number | null;
      accessibilityScore: number | null;
      seoScore: number | null;
      bestPracticesScore: number | null;
      lcpMs: number | null;
      tbtMs?: number | null;
      /** @deprecated Legacy API field; it is lab TBT until field INP is wired. */
      inpMs: number | null;
      cls: string | null;
      fcpMs: number | null;
      ttfbMs: number | null;
      auditedAt: string | null;
    } | null;
  };
  recommendations: Array<{
    category: string;
    description: string;
    impact: "high" | "medium" | "low";
  }>;
}

export function usePagePerformance(pageId: string | undefined) {
  const [performance, setPerformance] = useState<PagePerformance | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pageId) {
      setPerformance(null);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    apiFetch<{ data: OptimizationAnalysisResponse }>(`/crm/pages/${pageId}/audits/analysis`)
      .then((res) => setPerformance(mapAnalysisToPagePerformance(res.data)))
      .catch((fetchError) => {
        setPerformance(null);
        setError(toErrorMessage(fetchError, "Failed to load the canonical optimization analysis."));
      })
      .finally(() => setIsLoading(false));
  }, [pageId]);

  return { performance, isLoading, error };
}

export async function runPageAudit(pageId: string): Promise<void> {
  throw new Error(
    `Manual page audit trigger is not available for ${pageId}. Publish queues Lighthouse audits; use /crm/pages/${pageId}/audits/analysis for the canonical optimization view.`,
  );
}

// ---------------------------------------------------------------------------
// C125: GBP Lookup
// ---------------------------------------------------------------------------

export interface GbpBusinessData {
  name: string;
  formattedAddress: string;
  phone: string | null;
  openingHours: Array<{ day: string; hours: string }>;
  photos: string[];
  categories: string[];
  services: string[];
  reviews: Array<{ author: string; rating: number; text: string }>;
  description: string | null;
  rating: number | null;
  reviewCount: number;
}

export function useGbpLookup() {
  const [data, setData] = useState<GbpBusinessData | null>(null);
  const [isLooking, setIsLooking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lookup = useCallback(async (businessName: string, location?: string) => {
    setIsLooking(true);
    setError(null);
    try {
      throw buildUnsupportedBuilderError(
        "GBP lookup is not mounted on this builder seam. Use the Tell Me flow business enrichment checkpoint path instead.",
      );
    } catch (err) {
      const message = toErrorMessage(err, "GBP lookup failed.");
      setData(null);
      setError(message);
      return null;
    } finally {
      setIsLooking(false);
    }
  }, []);

  return { data, isLooking, error, lookup };
}

// ---------------------------------------------------------------------------
// C126: AI Interview (streaming generation)
// ---------------------------------------------------------------------------

export interface InterviewData {
  businessType: string;
  businessName: string;
  gbpData: GbpBusinessData | null;
  userOverrides: Record<string, string>;
}

export function useAiInterview() {
  const [step, setStep] = useState(0);
  const [interviewData, setInterviewData] = useState<Partial<InterviewData>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [sections, setSections] = useState<Array<{ id: string; type: string; content: unknown; status: "generating" | "ready" }>>([]);
  const [error, setError] = useState<string | null>(null);

  const setBusinessType = useCallback((type: string) => {
    setInterviewData((prev) => ({ ...prev, businessType: type }));
    setStep(1);
  }, []);

  const setBusinessName = useCallback((name: string) => {
    setInterviewData((prev) => ({ ...prev, businessName: name }));
    setStep(2);
  }, []);

  const confirmGbpData = useCallback((gbpData: GbpBusinessData | null, overrides?: Record<string, string>) => {
    setInterviewData((prev) => ({ ...prev, gbpData, userOverrides: overrides ?? {} }));
    setStep(3);
  }, []);

  const generate = useCallback(async () => {
    setIsGenerating(true);
    setError(null);
    try {
      throw buildUnsupportedBuilderError(
        "The legacy AI interview generate route is not mounted. Use /pages/ai-generate or /pages/streaming-generator for real checkpoint-backed drafting.",
      );
    } catch (generationError) {
      setError(toErrorMessage(generationError, "Failed to generate interview sections."));
    } finally {
      setIsGenerating(false);
    }
  }, [interviewData]);

  return { step, interviewData, sections, isGenerating, error, setBusinessType, setBusinessName, confirmGbpData, generate };
}

// ---------------------------------------------------------------------------
// C127: Section review (approve / change / redo)
// ---------------------------------------------------------------------------

export async function editSection(sectionId: string, instruction: string): Promise<unknown> {
  throw buildUnsupportedBuilderError(
    `Legacy section edit is not mounted for ${sectionId}. Use checkpoint modify flows from the active builder surfaces instead.`,
  );
}

export async function redoSection(sectionId: string): Promise<unknown> {
  throw buildUnsupportedBuilderError(
    `Legacy section redo is not mounted for ${sectionId}. Use checkpoint modify flows from the active builder surfaces instead.`,
  );
}

// ---------------------------------------------------------------------------
// C128: Publish flow
// ---------------------------------------------------------------------------

export async function publishWithSubdomain(pageId: string, slug: string, options: {
  connectBooking?: boolean;
  connectContactForm?: boolean;
}): Promise<{ url: string }> {
  void slug;
  void options;
  throw buildUnsupportedBuilderError(
    `Direct publish-with-subdomain is not mounted for ${pageId}. Use the canonical page or site publish flow from the mounted pages workspace.`,
  );
}

// ---------------------------------------------------------------------------
// C133: Builder tour (localStorage-based)
// ---------------------------------------------------------------------------

const TOUR_KEY = "builder_tour_completed";

export function useBuilderTour() {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const completed = localStorage.getItem(TOUR_KEY);
    if (!completed) setIsActive(true);
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => prev + 1);
  }, []);

  const skip = useCallback(() => {
    setIsActive(false);
    localStorage.setItem(TOUR_KEY, "true");
  }, []);

  const complete = useCallback(() => {
    setIsActive(false);
    localStorage.setItem(TOUR_KEY, "true");
  }, []);

  return { isActive, currentStep, nextStep, skip, complete };
}

function mapAnalysisToPagePerformance(
  analysis: OptimizationAnalysisResponse,
): PagePerformance {
  const audit = analysis.performance.audit;

  return {
    pageId: analysis.pageId ?? "",
    pageName: null,
    performanceScore: audit?.performanceScore ?? null,
    accessibilityScore: audit?.accessibilityScore ?? null,
    seoScore: audit?.seoScore ?? null,
    bestPracticesScore: audit?.bestPracticesScore ?? null,
    lcpMs: audit?.lcpMs ?? null,
    tbtMs: audit?.tbtMs ?? audit?.inpMs ?? null,
    inpMs: audit?.inpMs ?? null,
    cls: audit?.cls != null ? Number(audit.cls) : null,
    fcpMs: audit?.fcpMs ?? null,
    ttfbMs: audit?.ttfbMs ?? null,
    auditedAt: audit?.auditedAt ?? null,
    recommendations: analysis.recommendations
      .filter((item) => item.category === "performance")
      .map((item) => ({
        category: item.category,
        message: item.description,
        impact: item.impact,
      })),
  };
}
